import { useState } from "react";
import { toast } from "sonner";
import { GearSix, MagnifyingGlass } from "@phosphor-icons/react";
import api from "@/lib/api";

const STATUS_STYLES = {
  active: "bg-black text-white",
  trialing: "bg-black text-white",
  past_due: "bg-amber-100 text-amber-800 border border-amber-300",
  canceled: "bg-zinc-200 text-zinc-600",
  unpaid: "bg-red-100 text-red-700 border border-red-300",
};

export default function ManageBilling() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);

  const check = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await api.get(`/stripe/subscription-status`, { params: { email } });
      setStatus(res.data);
      setChecked(true);
    } catch {
      toast.error("Could not look up subscription.");
    } finally {
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setLoading(true);
    try {
      const res = await api.post("/stripe/billing-portal", { email, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open billing portal.");
      setLoading(false);
    }
  };

  const inputCls = "flex-1 bg-white border border-black/15 px-4 py-3 outline-none focus:border-black transition-colors text-sm";

  return (
    <div className="border border-black/10 bg-white p-8 sm:p-10" data-testid="manage-billing">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-zinc-400"><GearSix size={14} /> Existing subscribers</div>
      <h3 className="mt-3 font-display text-2xl sm:text-3xl font-black tracking-tighter">Manage your subscription</h3>
      <p className="mt-2 text-zinc-600">Update your card, view invoices, or cancel anytime. Enter the email you subscribed with.</p>

      <form onSubmit={check} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-xl">
        <input type="email" required data-testid="manage-email" className={inputCls} value={email} onChange={(e) => { setEmail(e.target.value); setChecked(false); }} placeholder="you@business.com" />
        <button type="submit" disabled={loading} data-testid="manage-check-btn" className="inline-flex items-center justify-center gap-2 rounded-full border border-black/20 font-medium px-5 py-3 hover:bg-black hover:text-white transition-colors disabled:opacity-50">
          <MagnifyingGlass size={18} /> Find subscription
        </button>
      </form>

      {checked && status && (
        <div className="mt-6" data-testid="manage-status">
          {status.found ? (
            <div className="flex flex-wrap items-center gap-4">
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${STATUS_STYLES[status.status] || "bg-zinc-200 text-zinc-700"}`}>
                {status.status?.replace("_", " ")}
              </span>
              <span className="text-sm text-zinc-600">
                {status.plan} plan{status.current_period_end ? <> · renews {new Date(status.current_period_end).toLocaleDateString()}</> : null}
              </span>
              <button onClick={openPortal} disabled={loading} data-testid="manage-portal-btn" className="ml-auto inline-flex items-center gap-2 rounded-full bg-black text-white font-medium px-5 py-2.5 hover:bg-zinc-800 transition-colors disabled:opacity-50">
                Manage Billing
              </button>
            </div>
          ) : (
            <p className="text-sm text-zinc-500">No subscription found for that email.</p>
          )}
        </div>
      )}
    </div>
  );
}
